import {Trans} from '@lingui/macro';
import React, {FC, RefObject, useRef, useState} from 'react';
import {animated, Spring} from 'react-spring';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {UserTheme, useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useTimeout} from '../../../../srcCommon/helpers/hookHelpers';
import {buildTeammateDisplayName, inboxHasColor} from '../../../helpers/modelHelpers';
import {
  ConversationSortPropertiesEnum,
  ConversationTeamStatusesEnum,
} from '../../../models/api/conversationCommonTypes';
import {ConversationModel} from '../../../models/api/conversationModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {ContactOrAccountDetailsPanelId} from '../../../store/panels/panelStateBase';
import {Avatar} from '../../common/avatars/avatar';
import {AvatarBackground} from '../../common/avatars/avatarBackground';
import {TeammateAvatar} from '../../common/avatars/teammateAvatar';
import {ColorDot} from '../../common/colorDot';
import {InteractiveDateTimeFormatterContainer} from '../../common/formatters/dateTime/interactiveDateTimeFormatterContainer';
import {ConversationCardTagsContainer} from './conversationCardTagsContainer';
import {ConversationPreviewContainer} from './conversationPreviewContainer';

/*
 * Constants.
 */

const inboxColorDotSize = 8;
const maxParticipantCount = 3;
const maxOverflowCount = 9;

// Controls how long someone would have to hover over the item to be reported.
export const hoverOverTrackingTime = 1000;

/*
 * Types.
 */

interface ConversationCardProps {
  panelId: ContactOrAccountDetailsPanelId;
  conversation: ConversationModel;
  inbox: InboxModel | undefined;
  assignee: TeammateModel | undefined;
  participants: Array<TeammateModel>;
  shouldHighlight: boolean;
  viewerConversationId: number | undefined;
  reminderDate: number | undefined;
  openConversation: HandlerOf<ConversationModel>;
  onClick: HandlerOf<ConversationModel>;
  onConversationHoverTrack?: Handler;
}

/*
 * Style.
 */

const makeHighlightedCardStyle = (theme: UserTheme) => ({
  backgroundColor: theme.palette.teal.shade10,
});

const makeUnhighlightedCardStyle = (theme: UserTheme) => ({
  backgroundColor: theme.greys.white,
});

const highlightDelay = 1000; // Milliseconds
const highlightDuration = 1000; // Milliseconds
export const highlightTotalTime = highlightDelay + highlightDuration;

interface StyledConversationCardDivStyleProps {
  $isViewerConversation: boolean;
}

const StyledConversationCardDiv = styled.div<StyledConversationCardDivStyleProps>`
  display: flex;
  flex-direction: column;
  ${(p) => boxShadow(p.theme, `0px 1px 1px ${p.theme.alphas.black10}`)};
  border: ${(p) =>
    p.$isViewerConversation
      ? `2px solid ${p.theme.palette.blue.shade40}`
      : `1px solid ${p.theme.alphas.black30}`};
  border-radius: 8px;
  padding: 14px 16px 16px 16px;

  &:not(:last-child) {
    margin-bottom: 12px;
  }

  &:hover {
    background: ${(p) => p.theme.greys.shade10};
  }
`;
const AnimatedStyledConversationCardDiv = animated(StyledConversationCardDiv);

const StyledRowDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:not(:last-child) {
    margin-bottom: 6px;
  }
`;

const StyledColorDot = styled(ColorDot)`
  margin-right: 8px;
  flex-shrink: 0;
`;

const StyledInboxNameSpan = styled.span`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
  ${ellipsis()}
`;

const StyledInboxDiv = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const StyledTeammatesDiv = styled.div`
  display: flex;
  align-items: center;
  margin-left: 4px;
`;

const StyledParticipantsDiv = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
`;

const StyledOverflowCountDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  color: ${(p) => p.theme.greys.white};
  font-size: 9px;
  font-weight: ${fontWeights.bold};
  text-align: center;
  line-height: 16px;
  letter-spacing: -1px;
`;

const StyledAssigneeTeammateAvatar = styled(TeammateAvatar)`
  border: 1px solid ${(p) => p.theme.greys.white};
  border-radius: 50%;
  ${(p) => boxShadow(p.theme, `0 0 0 1px ${p.theme.palette.blue.shade40}`)};
  box-sizing: content-box;
  margin-left: 4px;
`;

const StyledParticipantAvatarWrapperDiv = styled.div`
  margin-left: -1px;
`;

const StyledSubjectDiv = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const StyledArchivedIcon = styled(Icon)`
  margin-right: 6px;
`;

const StyledSubjectNameDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade90};
  ${ellipsis()}
`;

interface DateTimeFormatterContainerStyleProps {
  $isReminder: boolean;
}
const StyledDateTimeFormatterContainerDiv = styled.div<DateTimeFormatterContainerStyleProps>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-left: 4px;
  font-size: ${fontSizes.veryTiny};
  color: ${(p) => p.theme.greys.shade70};
  color: ${(p) => (p.$isReminder ? `${p.theme.palette.orange.shade40}` : `${p.theme.greys.shade70}`)};
  text-transform: uppercase;
`;

const StyledClockIcon = styled(Icon)`
  margin-right: 4px;
`;

/*
 * Component.
 */

export const ConversationCard: FC<ConversationCardProps> = (props) => {
  const {
    panelId,
    conversation,
    inbox,
    assignee,
    participants,
    shouldHighlight,
    viewerConversationId,
    reminderDate,
    openConversation,
    onClick,
    onConversationHoverTrack,
  } = props;

  const theme = useUserTheme();
  const cardDivRef = useRef<HTMLDivElement>(null);
  const [shouldShowPreview, setShouldShowPreview] = useState(false);
  const [safeSetTimeout] = useTimeout();

  const onClickHandler = () => {
    openConversation(conversation);
    onClick(conversation);
  };
  const isViewerConversation = viewerConversationId === conversation.id;

  const onConversationEnter = () => {
    setShouldShowPreview(true);

    if (onConversationHoverTrack) {
      safeSetTimeout(() => {
        onConversationHoverTrack();
      }, hoverOverTrackingTime);
    }
  };

  const onConversationExit = () => {
    if (shouldShowPreview) {
      setShouldShowPreview(false);
    }
  };

  if (shouldHighlight) {
    return (
      <Spring
        from={makeHighlightedCardStyle(theme)}
        to={makeUnhighlightedCardStyle(theme)}
        delay={highlightDelay}
        config={{duration: highlightDuration}}
      >
        {(style) => (
          <AnimatedStyledConversationCardDiv
            style={style}
            onClick={onClickHandler}
            $isViewerConversation={isViewerConversation}
            onMouseEnter={() => {
              onConversationEnter();
            }}
            onMouseLeave={() => {
              onConversationExit();
            }}
            ref={cardDivRef}
          >
            {renderInboxAndTeammatesRow(inbox, theme, assignee, participants)}
            {renderSubjectAndDateRow(conversation, reminderDate)}
            <ConversationCardTagsContainer conversation={conversation} />
            {renderPreview(panelId, shouldShowPreview, cardDivRef, props)}
          </AnimatedStyledConversationCardDiv>
        )}
      </Spring>
    );
  }

  return (
    <StyledConversationCardDiv
      {...buildInteractionIdProps(`contact-details-conversation-card-${conversation.id}`)}
      onClick={onClickHandler}
      $isViewerConversation={isViewerConversation}
      onMouseEnter={() => {
        onConversationEnter();
      }}
      onMouseLeave={() => {
        onConversationExit();
      }}
      ref={cardDivRef}
    >
      {renderInboxAndTeammatesRow(inbox, theme, assignee, participants)}
      {renderSubjectAndDateRow(conversation, reminderDate)}
      <ConversationCardTagsContainer conversation={conversation} />
      {renderPreview(panelId, shouldShowPreview, cardDivRef, props)}
    </StyledConversationCardDiv>
  );
};

/*
 * Helpers.
 */

function renderPreview(
  panelId: ContactOrAccountDetailsPanelId,
  shouldShowPreview: boolean,
  cardDivRef: RefObject<HTMLDivElement>,
  props: ConversationCardProps,
) {
  if (!shouldShowPreview || !cardDivRef.current) {
    return null;
  }

  return (
    <ConversationPreviewContainer
      panelId={panelId}
      conversation={props.conversation}
      anchor={cardDivRef}
      viewerConversationId={props.viewerConversationId}
    />
  );
}

function renderInboxAndTeammatesRow(
  inbox: InboxModel | undefined,
  theme: UserTheme,
  assignee: TeammateModel | undefined,
  participants: Array<TeammateModel>,
) {
  return (
    <StyledRowDiv>
      {renderInbox(inbox, theme)}
      {renderTeammates(participants, assignee)}
    </StyledRowDiv>
  );
}

function renderInbox(inbox: InboxModel | undefined, theme: UserTheme) {
  const defaultColor = theme.itemDot;

  if (!inbox) {
    return (
      <StyledInboxDiv>
        <StyledColorDot size={inboxColorDotSize} color={defaultColor} />
        <StyledInboxNameSpan>
          <Trans id="contact-details-conversation-card-shared-with-me">Shared with me</Trans>
        </StyledInboxNameSpan>
      </StyledInboxDiv>
    );
  }

  const inboxColor = (inboxHasColor(inbox) && inbox.color) || undefined;
  return (
    <StyledInboxDiv>
      <StyledColorDot size={inboxColorDotSize} color={inboxColor || defaultColor} />
      <StyledInboxNameSpan>{inbox.name}</StyledInboxNameSpan>
    </StyledInboxDiv>
  );
}

function renderTeammates(participants: Array<TeammateModel>, assignee: TeammateModel | undefined) {
  const participantCount = participants.length;
  const hasAssignee = Boolean(assignee);
  const totalParticipantCount = hasAssignee ? participantCount + 1 : participantCount;

  const hasOverflow = totalParticipantCount > maxParticipantCount;
  const visibleParticipantCount = computeVisibleParticipantCount(Boolean(assignee), hasOverflow);
  const overflowCount = participantCount - visibleParticipantCount;

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledTeammatesDiv>
          <StyledParticipantsDiv>
            {hasOverflow && renderOverflowCount(overflowCount)}
            {participants.slice(0, visibleParticipantCount).map((participant) => (
              <StyledParticipantAvatarWrapperDiv key={participant.id}>
                <TeammateAvatar
                  teammate={participant}
                  size={VisualSizesEnum.SMALL}
                  borderColor={theme.greys.white}
                />
              </StyledParticipantAvatarWrapperDiv>
            ))}
          </StyledParticipantsDiv>

          {assignee && renderAssignee(assignee)}
        </StyledTeammatesDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderAssignee(assignee: TeammateModel) {
  return (
    <TooltipCoordinator
      placement="top"
      render={() => (
        <Trans id="contact-details-conversation-card-assigned-to">
          Assigned to {buildTeammateDisplayName(assignee)}
        </Trans>
      )}
    >
      <StyledAssigneeTeammateAvatar teammate={assignee} size={VisualSizesEnum.SMALL} />
    </TooltipCoordinator>
  );
}

function computeVisibleParticipantCount(hasAssignee: boolean, hasOverflow: boolean) {
  if (hasAssignee && hasOverflow) {
    return maxParticipantCount - 2;
  }

  if (hasOverflow || hasAssignee) {
    return maxParticipantCount - 1;
  }

  return maxParticipantCount;
}

function renderOverflowCount(overflowCount: number) {
  const overflowCountText = overflowCount > maxOverflowCount ? `${maxOverflowCount}+` : overflowCount;
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledParticipantAvatarWrapperDiv>
          <Avatar size={VisualSizesEnum.SMALL}>
            <AvatarBackground color={theme.greys.shade70} />
            <StyledOverflowCountDiv>{overflowCountText}</StyledOverflowCountDiv>
          </Avatar>
        </StyledParticipantAvatarWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderSubjectAndDateRow(conversation: ConversationModel, reminderDate: number | undefined) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledRowDiv>
          <StyledSubjectDiv>
            {getIsArchived(conversation) && (
              <StyledArchivedIcon name="archiveSmall" color={theme.greys.shade70} />
            )}
            <StyledSubjectNameDiv>{conversation.subject || conversation.summary}</StyledSubjectNameDiv>
          </StyledSubjectDiv>
          {renderDate(conversation, reminderDate)}
        </StyledRowDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderDate(conversation: ConversationModel, reminderDate: number | undefined) {
  if (reminderDate) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledDateTimeFormatterContainerDiv $isReminder>
            <StyledClockIcon name="clockTiny" color={theme.palette.orange.shade40} />
            <InteractiveDateTimeFormatterContainer value={reminderDate} />
          </StyledDateTimeFormatterContainerDiv>
        )}
      </UserThemeConsumer>
    );
  }

  return (
    <StyledDateTimeFormatterContainerDiv $isReminder={false}>
      <InteractiveDateTimeFormatterContainer
        value={conversation.orders[ConversationSortPropertiesEnum.BUMPED_AT]}
      />
    </StyledDateTimeFormatterContainerDiv>
  );
}

function getIsArchived(conversation: ConversationModel) {
  return conversation.teamStatus === ConversationTeamStatusesEnum.ARCHIVED;
}
